import React, { useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useReducedMotion } from 'framer-motion';
import { useCopy } from '../../i18n/LocaleContext';
import { replica, useShortScreen } from './CoverPageTurn';
import { closingFoldAt, flapMatrix, turnProgress } from './coverPageTurnGeometry';
import './BackCoverTurn.css';

const COPY = {
  fr: { magazine: 'Le Placard à Doudous', issue: 'Hors-série · Les Naissances d’août' },
  en: { magazine: 'Le Placard à Doudous', issue: 'Special issue · Born in August' },
};

// Le pied de page en double, pour l'image : un simple bloc, pas un second <footer>.
function backcoverReplica(footer) {
  const copy = replica(footer);
  const plain = document.createElement('div');
  [...copy.attributes].forEach(({ name, value }) => plain.setAttribute(name, value));
  plain.append(...copy.childNodes);
  plain.classList.remove('is-closing');
  plain.inert = true;
  return plain;
}

// Le magazine se referme, en miroir de la couverture qui s'ouvre (CoverPageTurn). Au bas
// de l'accueil, la quatrième de couverture attend ouverte à plat à droite de l'écran,
// l'intérieur en l'air ; le temps d'un écran de défilement, elle pivote autour du dos, le
// bord droit, et se pose sur le mode d'emploi, qui continue de monter dessous. Le haut se
// pose d'abord, le coin bas gauche en dernier. Posée, c'est le vrai pied de page, à la
// place exacte où il arrive. Sans animation, ou sur un écran trop bas, le pied de page défile.
export default function BackCoverTurn() {
  const c = useCopy(COPY);
  const stageRef = useRef(null);
  const reduceMotion = useReducedMotion();
  const short = useShortScreen();
  const live = !reduceMotion && !short;

  useLayoutEffect(() => {
    const stage = stageRef.current;
    const footer = document.querySelector('footer.backcover');
    const page = document.querySelector('[data-testid="practical-section"]')?.closest('main');
    if (!live || !stage || !footer) return undefined;

    const part = selector => stage.querySelector(selector);
    const sheet = part('.back-turn-sheet'), side = part('.back-turn-side'), flat = side.firstElementChild;
    const shadow = part('.back-turn-shadow'), back = part('.back-turn-back');
    const shade = part('.back-turn-shade'), crease = part('.back-turn-crease');
    const front = part('.back-turn-front');

    // S : le haut du pied de page touche le bas de l'écran. E : il touche le haut de l'écran,
    // ou la page ne descend plus ; il est alors à `drop` pixels du haut, et la page s'y pose.
    let W = 0, H = 0, L = 0, S = 0, E = 0, T = 0, drop = 0;
    let shown = '', closing = false, shift = NaN;

    const render = () => {
      const s = window.scrollY;
      // Un demi-pixel avant le bas, c'est déjà la vraie page : le défilement s'y arrête parfois.
      const now = s > S && s < E - 0.5;
      if (now !== closing) {
        closing = now;
        // Pendant le geste, le vrai pied de page attend dessous, couvert du bleu du mode d'emploi.
        stage.classList.toggle('is-closing', now);
        footer.classList.toggle('is-closing', now);
        shown = '';
      }
      if (!now) return;
      // Placée, pas déplacée : à la même fraction de pixel que le vrai, elle est peinte comme lui.
      // Si le navigateur décale ce qui est fixe (Safari mobile), la page suit ce décalage.
      const offset = stage.getBoundingClientRect().top;
      if (offset !== shift) {
        shift = offset;
        sheet.style.top = `${drop - offset}px`;
      }
      const q = turnProgress(s - S, T);
      const key = String(q);
      if (key === shown) return;
      shown = key;
      const fold = closingFoldAt(q, W, H);
      // Le dernier demi-pixel posé, la page est à plat : plus de pli à dessiner, le double reste
      // net jusqu'au relais au lieu de passer par deux rotations qui s'annulent.
      stage.classList.toggle('is-landed', fold.origin.x < 0.5);
      const { x, y } = fold.origin;
      const angle = fold.tilt * 180 / Math.PI;
      const at = `translate(${x}px, ${y}px) rotate(${angle}deg)`;
      side.style.transform = `${at} translate(${-L}px, ${-L}px)`;
      flat.style.transform = `translate(${L}px, ${L}px) rotate(${-angle}deg) translate(${-x}px, ${-y}px)`;
      // Les mêmes ombres que la couverture : elles s'élargissent quand la page se soulève.
      const lift = Math.sin(Math.PI * q);
      shadow.style.transform = `${at} translate(0, ${-L}px) scaleX(${1.5 * lift})`;
      shadow.style.opacity = String(Math.min(1, q * 5));
      shade.style.opacity = String(lift);
      back.style.transform = `matrix(${flapMatrix(fold, W).join(', ')})`;
      crease.style.transform = `translate(${W - x}px, ${y}px) rotate(${180 - angle}deg) translate(0, ${-L}px) scaleX(${1.7 * lift})`;
    };

    const layout = () => {
      const root = document.documentElement;
      const vh = root.clientHeight;
      W = root.clientWidth;
      // Le pied de page est hors de <main> : son entrée glissée ne le déplace pas.
      const top = footer.getBoundingClientRect().top + window.scrollY;
      // Arrondi comme le défilement du navigateur : la page se pose là où le vrai arrivera.
      E = Math.min(Math.round(top), root.scrollHeight - vh);
      S = top - vh;
      drop = top - E;
      H = vh - drop;
      T = E - S;
      L = Math.ceil(Math.hypot(W, H)) + 120;
      stage.style.setProperty('--turn-w', `${W}px`);
      stage.style.setProperty('--turn-h', `${H}px`);
      stage.style.setProperty('--turn-l', `${L}px`);
      shift = NaN;
      shown = '';
      render();
    };

    let copied = 0;
    const copy = () => {
      copied = 0;
      front.replaceChildren(backcoverReplica(footer));
      front.querySelectorAll('img').forEach(img => img.decode?.().catch(() => {}));
      layout();
    };
    copy();
    // On refait le double quand le pied de page change (la langue), pas pour notre propre classe.
    const changed = new MutationObserver(records => {
      if (records.every(r => r.target === footer && r.attributeName === 'class')) return;
      if (!copied) copied = requestAnimationFrame(copy);
    });
    changed.observe(footer, { subtree: true, childList: true, characterData: true, attributes: true });

    const resize = new ResizeObserver(layout);
    resize.observe(footer);
    if (page) resize.observe(page);
    let mounted = true;
    document.fonts?.ready.then(() => { if (mounted) layout(); });
    window.addEventListener('resize', layout);

    // Lenis déplace la page dans sa propre image : on suit son événement, comme la couverture.
    let lenis = null, quitLenis = null;
    const followLenis = () => {
      if (window.lenis === lenis) return;
      quitLenis?.();
      lenis = window.lenis || null;
      quitLenis = lenis?.on('scroll', render) || null;
    };
    const onScroll = () => { followLenis(); render(); };
    followLenis();
    window.addEventListener('scroll', onScroll, { passive: true });

    // Au clavier, rien ne se cache sous la page qui se pose : entrer dans le pied de page
    // la pose tout de suite ; revenir dans le mode d'emploi la rouvre.
    let refocus = 0;
    const jump = y => {
      if (window.lenis) window.lenis.scrollTo(y, { immediate: true, force: true });
      else window.scrollTo(0, y);
      render();
    };
    const settle = () => {
      const s = window.scrollY;
      if (s >= E - 0.5) return;
      if (footer.contains(document.activeElement)) jump(E);
      else if (s > S && page?.contains(document.activeElement)) jump(S);
    };
    const onFocus = event => {
      if (!event.target.matches?.(':focus-visible')) return;
      settle();
      cancelAnimationFrame(refocus);
      refocus = requestAnimationFrame(settle);
    };
    document.addEventListener('focusin', onFocus);

    return () => {
      mounted = false;
      cancelAnimationFrame(copied);
      cancelAnimationFrame(refocus);
      changed.disconnect();
      resize.disconnect();
      quitLenis?.();
      window.removeEventListener('resize', layout);
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('focusin', onFocus);
      front.replaceChildren();
      stage.classList.remove('is-closing', 'is-landed');
      footer.classList.remove('is-closing');
    };
  }, [live]);

  if (!live) return null;
  return createPortal(
    <div ref={stageRef} className="back-turn" aria-hidden="true">
      <div className="back-turn-sheet">
        {/* L'ombre de la page soulevée sur le mode d'emploi, de l'autre côté du pli. */}
        <div className="back-turn-shadow" />
        {/* La partie déjà posée (le pied de page, en double) et le rabat encore levé. */}
        <div className="back-turn-side">
          <div className="back-turn-flat">
            <div className="back-turn-front" />
            <div className="back-turn-back">
              <div className="back-turn-shade" />
              <div className="back-turn-paper">
                <div className="back-turn-colophon">
                  <p className="back-turn-colophon-name">{c.magazine}</p>
                  <p>{c.issue}</p>
                </div>
                <div className="back-turn-crease" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
